import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { registerCurrencyPrecision } from "@/lib/currency";

/**
 * Feeds the currency precision registry from the database so every screen
 * (and every email, via the backend mirror) renders an asset at the precision
 * its configuration declares.
 *
 * Mounted once from BalanceSyncProvider.
 */
export function useCurrencyPrecision() {
  const tokens = useQuery({
    queryKey: ["currency-precision", "tokens"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("token_configs")
        .select("symbol, price_precision, quantity_precision");
      if (error) throw error;
      return data || [];
    },
    staleTime: 600_000,
  });

  const fiat = useQuery({
    queryKey: ["currency-precision", "fiat"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fiat_currencies")
        .select("currency_code")
        .eq("is_active", true);
      if (error) throw error;
      return data || [];
    },
    staleTime: 600_000,
  });


  useEffect(() => {
    if (!tokens.data?.length) return;
    registerCurrencyPrecision(
      tokens.data.map((t: any) => ({
        currency: t.symbol,
        decimals: t.quantity_precision,
        quantityDecimals: t.quantity_precision,
        priceDecimals: t.price_precision,
      })),
    );
  }, [tokens.data]);

  useEffect(() => {
    if (!fiat.data?.length) return;
    registerCurrencyPrecision(
      // ISO 4217 minor units are applied automatically; the registry just
      // declares which codes are fiat (the platform serves 120+).
      fiat.data.map((f: any) => ({ currency: f.currency_code, isFiat: true })),
    );
  }, [fiat.data]);
}
